
import React from 'react';
import { Star, AlertTriangle } from 'lucide-react';

interface ProductProps {
  product: {
    id: string | number;
    title: string;
    description: string | null;
    tags?: string[]; // Make tags optional
    image: string | null;
    rating?: number;
  }
}

const ProductCard: React.FC<ProductProps> = ({ product }) => {
  // Debug console for data verification
  console.log(`ProductCard rendering for id:${product?.id}`, product);
  
  // Defensive check for invalid product data
  if (!product || !product.id) {
    console.error('ProductCard received invalid product data:', product);
    return (
      <div className="product-card bg-white dark:bg-gray-800 rounded-lg overflow-hidden border border-red-200 dark:border-red-800 p-4">
        <div className="flex items-center justify-center">
          <AlertTriangle className="text-red-500 mr-2" />
          <span>Invalid product data</span>
        </div>
      </div>
    );
  }
  
  // Add fallback handling for product properties with more detailed logging
  const {
    title = 'Untitled Product',
    description = null,
    tags = [],
    image = null,
    rating
  } = product;

  // Enhanced error handling for image loading
  const [imageError, setImageError] = React.useState(false);
  const placeholderImage = '/placeholder.svg';

  return (
    <div className="product-card bg-white dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-100 dark:border-gray-700 shadow-md hover:shadow-lg transition-shadow">
      <div className="h-40 overflow-hidden bg-gray-100 dark:bg-gray-700 relative">
        {imageError ? (
          <div className="flex items-center justify-center h-full bg-gray-200 dark:bg-gray-700">
            <AlertTriangle size={24} className="text-gray-400" />
          </div>
        ) : (
          <img 
            src={image || placeholderImage} 
            alt={title}
            className="w-full h-full object-cover"
            onError={(e) => {
              console.log(`Image error for ${title}, using placeholder`);
              setImageError(true);
              e.currentTarget.src = placeholderImage;
            }}
          />
        )}
      </div>
      
      <div className="p-5">
        <h3 className="font-semibold text-lg">{title}</h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm mt-2 line-clamp-2">
          {description || 'No description available'}
        </p>
        
        <div className="mt-4 flex flex-wrap gap-2">
          {Array.isArray(tags) && tags.length > 0 ? tags.map((tag, index) => (
            <span key={index} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs">
              {tag}
            </span>
          )) : (
            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs text-gray-500">
              No tags
            </span>
          )}
        </div>
        
        <div className="mt-4 flex items-center justify-between">
          {rating !== undefined && rating !== null ? (
            <div className="flex items-center">
              <Star size={16} className="text-yellow-500 fill-yellow-500" />
              <span className="ml-1 text-sm font-medium">{rating}</span>
            </div>
          ) : (
            <div className="flex items-center">
              <Star size={16} className="text-gray-300" />
              <span className="ml-1 text-sm font-medium text-gray-500">No rating</span>
            </div>
          )}
          
          <div className="flex space-x-2">
            {Array.isArray(tags) && tags.includes('Free') && (
              <span className="px-3 py-1 text-sm bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-100 rounded-md font-medium">
                Free
              </span>
            )}
            {Array.isArray(tags) && tags.includes('Open Source') && (
              <span className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-100 rounded-md font-medium">
                Open Source
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
